import React, { useState } from 'react';
import styled from 'styled-components';
import StripeCheckoutButton from '../StripeCheckoutButton';
import { formatCurrency } from '../../utils/formatters';
import toast from 'react-hot-toast';

const StepContainer = styled.div`
  padding: 2rem;
`;

const StepContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const SectionTitle = styled.h3`
  color: #333;
  font-size: 1.5rem;
  margin-bottom: 2rem;
  font-weight: 600;
  text-align: center;
`;

const PaymentContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 350px;
  gap: 3rem;
  align-items: start;
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const OrderSummaryCard = styled.div`
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 24px;
  position: sticky;
  top: 20px;
  
  h4 {
    margin: 0 0 16px 0;
    color: #374151;
    font-size: 18px;
    font-weight: 600;
  }
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  
  &.total {
    padding-top: 12px;
    border-top: 1px solid #e5e7eb;
    font-weight: 600;
    font-size: 18px;
    color: #111827;
  }
  
  span:first-child {
    color: #6b7280;
  }
  
  span:last-child {
    color: #111827;
    font-weight: 500;
  }
`;

const SecurityBadges = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 16px;
  flex-wrap: wrap;
`;

const SecurityBadge = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #059669;
  font-weight: 500;
  padding: 4px 8px;
  background: #d1fae5;
  border-radius: 4px;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 2rem;
`;

const Button = styled.button`
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  
  &.secondary {
    background: #f3f4f6;
    color: #374151;
    border: 1px solid #d1d5db;
    
    &:hover:not(:disabled) {
      background: #e5e7eb;
    }
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ErrorContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  color: #dc2626;
  margin-bottom: 1rem;
  
  .icon {
    font-size: 1.2rem;
  }
`;

const SuccessContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 8px;
  color: #166534;
  margin-bottom: 1rem;
  
  .icon {
    font-size: 1.2rem;
  }
`;

const StripePaymentMethodStep = ({
    order,
    customerInfo,
    billingInfo,
    isProcessing,
    errors,
    onPaymentComplete,
    onPrev,
    onClearError
}) => {
    const [paymentError, setPaymentError] = useState(null);
    const [paymentSuccess, setPaymentSuccess] = useState(false);

    // Prepare order data for Stripe
    const orderData = {
        orderId: order?.id,
        totalAmount: order?.totalAmount || 0,
        currency: order?.currencyCode || 'eur',
        customerName: `${customerInfo?.firstName || ''} ${customerInfo?.lastName || ''}`.trim(),
        customerEmail: customerInfo?.email,
        customerPhone: customerInfo?.phone,
        billingAddress: billingInfo ? {
            line1: billingInfo.addressLine1,
            line2: billingInfo.addressLine2,
            city: billingInfo.city,
            postalCode: billingInfo.postalCode,
            country: billingInfo.country,
        } : null,
    };

    const handlePaymentSuccess = async (sessionData) => {
        try {
            setPaymentSuccess(true);
            toast.success('Payment successful! 🎉');

            // Call the parent's completion handler
            await onPaymentComplete({
                sessionId: sessionData?.sessionId,
                paymentMethod: 'stripe_checkout',
                status: 'completed',
            });

        } catch (error) {
            console.error('Error completing checkout:', error);
            setPaymentError('Payment was successful but there was an error completing your order. Please contact support.');
            toast.error('Error completing order');
        }
    };

    const handlePaymentError = (error) => {
        console.error('Payment error:', error);
        setPaymentError(error.message || 'Payment failed. Please try again.');
        toast.error('Payment failed');
    };

    const handleCancel = () => {
        if (window.confirm('Are you sure you want to go back? Your payment information will be cleared.')) {
            setPaymentError(null);
            setPaymentSuccess(false);
            onPrev();
        }
    };

    const clearPaymentError = () => {
        setPaymentError(null);
        onClearError?.('payment');
    };

    if (paymentSuccess) {
        return (
            <StepContainer>
                <StepContent>
                    <SuccessContainer>
                        <span className="icon">✅</span>
                        <span>Payment successful! Completing your order...</span>
                    </SuccessContainer>
                </StepContent>
            </StepContainer>
        );
    }

    return (
        <StepContainer>
            <StepContent>
                <SectionTitle>💳 Secure Payment</SectionTitle>

                {(paymentError || errors?.payment) && (
                    <ErrorContainer>
                        <span className="icon">⚠️</span>
                        <span>{paymentError || errors?.payment}</span>
                        <button
                            onClick={clearPaymentError}
                            style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'inherit', cursor: 'pointer' }}
                        >
                            ✕
                        </button>
                    </ErrorContainer>
                )}

                <PaymentContainer>
                    {/* Stripe Checkout Button */}
                    <div>
                        <div style={{ 
                            background: 'white',
                            border: '1px solid #e5e7eb',
                            borderRadius: '12px',
                            padding: '2rem',
                            textAlign: 'center'
                        }}>
                            <h4 style={{ 
                                margin: '0 0 1rem 0',
                                color: '#374151',
                                fontSize: '1.25rem',
                                fontWeight: '600'
                            }}>
                                Complete Your Payment
                            </h4>
                            <p style={{
                                color: '#6b7280',
                                margin: '0 0 2rem 0',
                                lineHeight: '1.5'
                            }}>
                                You'll be redirected to Stripe's secure checkout page to complete your payment.
                            </p>
                            <StripeCheckoutButton
                                orderData={orderData}
                                onSuccess={handlePaymentSuccess}
                                onError={handlePaymentError}
                                disabled={isProcessing}
                            />
                        </div>
                    </div>

                    {/* Order Summary */}
                    <OrderSummaryCard>
                        <h4>Order Summary</h4>

                        <SummaryRow>
                            <span>Subtotal:</span>
                            <span>{formatCurrency(order?.subtotal || 0, order?.currencyCode)}</span>
                        </SummaryRow>

                        {order?.taxAmount > 0 && (
                            <SummaryRow>
                                <span>Tax:</span>
                                <span>{formatCurrency(order?.taxAmount, order?.currencyCode)}</span>
                            </SummaryRow>
                        )}

                        {order?.feeAmount > 0 && (
                            <SummaryRow>
                                <span>Processing Fee:</span>
                                <span>{formatCurrency(order?.feeAmount, order?.currencyCode)}</span>
                            </SummaryRow>
                        )}

                        {order?.discountAmount > 0 && (
                            <SummaryRow>
                                <span>Discount:</span>
                                <span style={{ color: '#059669' }}>
                                    -{formatCurrency(order?.discountAmount, order?.currencyCode)}
                                </span>
                            </SummaryRow>
                        )}

                        <SummaryRow className="total">
                            <span>Total:</span>
                            <span>{formatCurrency(order?.totalAmount || 0, order?.currencyCode)}</span>
                        </SummaryRow>

                        <SecurityBadges>
                            <SecurityBadge>
                                🔒 SSL Encrypted
                            </SecurityBadge>
                            <SecurityBadge>
                                ✅ PCI Compliant
                            </SecurityBadge>
                            <SecurityBadge>
                                🛡️ Fraud Protected
                            </SecurityBadge>
                        </SecurityBadges>
                    </OrderSummaryCard>
                </PaymentContainer>

                <ActionButtons>
                    <Button
                        type="button"
                        className="secondary"
                        onClick={handleCancel}
                        disabled={isProcessing}
                    >
                        ← Back to Customer Info
                    </Button>
                </ActionButtons>
            </StepContent>
        </StepContainer>
    );
};

export default StripePaymentMethodStep;
